/** Dashboard — pixel-perfect 1:1 z prototype/dashboard.jsx DashboardMain.
 *  Real data přes TanStack Query (partner_dashboard_summary + Diese/Next + Invoices). */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { NewRequestModal } from "./new-request-modal";
import { KPICard } from "@/components/ui/kpi-card";
import { Ic } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { WeekRow } from "@/components/ui/week-row";
import { SectionHeading } from "@/components/ui/section-heading";
import { CardTable } from "@/components/ui/section-card-table";
import { MiniMap } from "@/components/ui/mini-map";
import { useAuth } from "@/auth/auth-provider";
import {
  enrichOrdersWithEndClient,
  fetchDashboardSummary,
  fetchOrders,
  isoWeekRange,
  toDateOnly,
} from "@/lib/fetchers";
import { fmtEur, fmtWeekday, fmtWeekdayLong, fmtWeekRange, fmtDateLong } from "@/lib/format";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";
import type { PartnerOrder } from "@/lib/types";

export function DashboardRoute() {
  const navigate = useNavigate();
  const { partner, impersonated } = useAuth();
  const [newRequestOpen, setNewRequestOpen] = useState(false);

  const summaryQ = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: fetchDashboardSummary,
  });

  const thisWeek = isoWeekRange(0);
  const nextWeek = isoWeekRange(1);

  const thisWeekQ = useQuery({
    queryKey: queryKeys.ordersWeek(toDateOnly(thisWeek.start)),
    queryFn: async () => {
      const orders = await fetchOrders({
        fromDate: toDateOnly(thisWeek.start),
        toDate: toDateOnly(thisWeek.end),
        orderBy: { column: "scheduled_date", ascending: true },
      });
      return enrichOrdersWithEndClient(orders);
    },
  });

  const nextWeekQ = useQuery({
    queryKey: queryKeys.ordersWeek(toDateOnly(nextWeek.start)),
    queryFn: async () => {
      const orders = await fetchOrders({
        fromDate: toDateOnly(nextWeek.start),
        toDate: toDateOnly(nextWeek.end),
        limit: 4,
        orderBy: { column: "scheduled_date", ascending: true },
      });
      return enrichOrdersWithEndClient(orders);
    },
  });

  const summary = summaryQ.data;
  const thisWeekOrders = thisWeekQ.data || [];
  const nextWeekOrders = nextWeekQ.data || [];

  const today = new Date();
  const eyebrow = `${fmtWeekdayLong(today)} · ${fmtDateLong(today)}`;
  const greetingName = partner?.contact_name?.split(" ")[0] || partner?.company_name || "";

  const openComplaints = summary?.open_complaints_count ?? 0;
  const openBalance = summary?.open_balance_eur ?? 0;
  const monthRevenue = summary?.this_month_revenue_eur ?? 0;
  const monthDone = summary?.this_month_done_count ?? 0;
  const thisWeekKpiCount = summary?.this_week_count ?? thisWeekOrders.length;

  return (
    <div style={{ padding: "32px 36px", maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          marginBottom: 28,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12.5,
              color: "var(--gold)",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
          <h1 className="display" style={{ margin: 0, fontSize: 32 }}>
            {t.dashboard.greetingMorning(greetingName)}
          </h1>
          <p style={{ marginTop: 8, color: "var(--text-2)", fontSize: 14 }}>
            {t.dashboard.weekSubtitle(thisWeekKpiCount, openComplaints)}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={() => navigate("/map")}>
            <Ic.Map size={14} /> {t.dashboard.actionsMap}
          </Button>
          {!impersonated && (
            <Button variant="primary" onClick={() => setNewRequestOpen(true)}>
              <Ic.Plus size={14} /> {t.dashboard.actionsNew}
            </Button>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
        <KPICard
          label={t.dashboard.kpi.thisWeek}
          value={thisWeekKpiCount.toString()}
          sub={`${t.dashboard.kpi.thisWeekSub} · ${fmtWeekRange(thisWeek.start, thisWeek.end)}`}
          icon={<Ic.Calendar size={16} />}
          accent
          onClick={() => navigate("/this-week")}
        />
        <KPICard
          label={t.dashboard.kpi.weOwe}
          value={fmtEur(openBalance)}
          sub={t.dashboard.kpi.weOweSubOpen(0)}
          icon={<Ic.Scale size={16} />}
          onClick={() => navigate("/balance")}
        />
        <KPICard
          label={t.dashboard.kpi.complaints}
          value={openComplaints.toString()}
          sub={t.dashboard.kpi.complaintsSubOpen}
          icon={<Ic.Alert size={16} />}
          onClick={() => navigate("/orders")}
        />
        <KPICard
          label={t.dashboard.kpi.revenue}
          value={fmtEur(monthRevenue)}
          sub={t.dashboard.kpi.revenueSubDone(monthDone)}
          icon={<Ic.Spark size={16} />}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr",
          gridTemplateRows: "auto auto",
          gap: 24,
          marginTop: 32,
        }}
      >
        <div style={{ gridColumn: "1 / 2", gridRow: "1 / 2" }}>
          <SectionHeading
            title={`${t.dashboard.sectionThisWeek} · ${fmtWeekRange(thisWeek.start, thisWeek.end)}`}
            linkLabel={t.dashboard.fullPlan}
            linkTo="/this-week"
            style={{ marginTop: 0 }}
          />
          <CardTable>
            {thisWeekOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: "24px 18px",
                    textAlign: "center",
                    color: "var(--text-3)",
                  }}
                >
                  {t.orderList.empty}
                </td>
              </tr>
            ) : (
              thisWeekOrders.map((o) => (
                <WeekRow
                  key={o.id}
                  item={mapOrderToRow(o)}
                  onOpen={(it) => navigate(`/orders/${it.orderNr}`)}
                  dense
                />
              ))
            )}
          </CardTable>
        </div>

        <div style={{ gridColumn: "1 / 2", gridRow: "2 / 3" }}>
          <SectionHeading
            title={`${t.dashboard.sectionNextWeek} · ${fmtWeekRange(nextWeek.start, nextWeek.end)}`}
            linkLabel={t.dashboard.planView}
            linkTo="/next-week"
            style={{ marginTop: 0 }}
          />
          <CardTable>
            {nextWeekOrders.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: "24px 18px",
                    textAlign: "center",
                    color: "var(--text-3)",
                  }}
                >
                  {t.orderList.empty}
                </td>
              </tr>
            ) : (
              nextWeekOrders.map((o) => (
                <WeekRow
                  key={o.id}
                  item={mapOrderToRow(o)}
                  onOpen={(it) => navigate(`/orders/${it.orderNr}`)}
                  dense
                />
              ))
            )}
          </CardTable>
        </div>

        <div
          style={{
            gridColumn: "2 / 3",
            gridRow: "1 / 3",
          }}
        >
          <SectionHeading
            title={t.nav.map}
            linkLabel={t.dashboard.actionsMap}
            linkTo="/map"
            style={{ marginTop: 0 }}
          />
          <MiniMap height={520} />
        </div>
      </div>

      <NewRequestModal open={newRequestOpen} onOpenChange={setNewRequestOpen} />
    </div>
  );
}

function mapOrderToRow(o: PartnerOrder) {
  const dateObj = o.scheduled_date ? new Date(o.scheduled_date) : null;
  return {
    orderNr: o.deal_number || o.id,
    day: dateObj ? fmtWeekday(dateObj) : "—",
    date: dateObj ? `${dateObj.getDate()}.${dateObj.getMonth() + 1}.` : "",
    time: o.scheduled_start_time ? o.scheduled_start_time.slice(0, 5) : null,
    client: o.installation_contact_name || o.end_client_label || o.title || "—",
    city: o.installation_city || "",
    system: o.system_type || "—",
    maker: o.device_label || undefined,
    value: o.value_eur || 0,
    status: o.status_label,
  };
}
